import React, { useState, useEffect } from "react";
import { AlertTriangle, Check, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

interface Contact {
  id?: string;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  [key: string]: any;
}

interface DuplicateGroup {
  existing: Contact;
  imported: Contact;
  action: "keep" | "replace" | "skip" | "merge";
}

interface DuplicateCheckerProps {
  duplicates: DuplicateGroup[];
  onDuplicatesResolved: (resolvedDuplicates: DuplicateGroup[]) => void;
}

const LoadingOverlay = ({ title, messages }: { title: string, messages: string[]; }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-gray-600">{messages[currentMessageIndex]}</p>
      </div>
    </div>
  );
};

const DuplicateChecker: React.FC<DuplicateCheckerProps> = ({
  duplicates: initialDuplicates,
  onDuplicatesResolved
}) => {
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>(initialDuplicates);
  const [isLoading, setIsLoading] = useState(false);

  const handleActionChange = (index: number, action: "keep" | "replace" | "skip" | "merge") => {
    const updatedDuplicates = [...duplicates];
    updatedDuplicates[index].action = action;
    setDuplicates(updatedDuplicates);
  };

  const handleApplyToAll = (action: "keep" | "replace" | "skip" | "merge") => {
    const updatedDuplicates = duplicates.map(dup => ({
      ...dup,
      action
    }));
    setDuplicates(updatedDuplicates);
  };

  const handleFinalize = async () => {
    setIsLoading(true);
    try {
      await onDuplicatesResolved(duplicates);
    } finally {
      setIsLoading(false);
    }
  };

  const totalDuplicates = duplicates.length;
  const resolvedCount = duplicates.filter(d => d.action !== "skip").length;

  if (isLoading) {
    return (
      <LoadingOverlay
        title="Importation en cours..."
        messages={[
          "On fait rentrer vos contacts en douceur...",
          "On leur prépare des petits lits douillets...",
          "On leur donne des badges d'entrée...",
          "On leur fait visiter la base de données...",
          "On leur sert le café d'accueil...",
          "On leur distribue les badges de visite...",
          "On leur montre où sont les toilettes...",
          "On leur explique le règlement intérieur...",
          "On leur fait signer le registre d'entrée...",
          "On leur donne les clés de leur bureau...",
          "On leur montre où est la machine à café...",
          "On leur fait visiter la salle de pause...",
          "On leur présente leurs nouveaux collègues...",
          "On leur distribue les cartes de cantine...",
          "On leur montre où sont les extincteurs..."
        ]}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 flex items-start">
        <AlertTriangle className="text-amber-500 h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <h3 className="font-medium text-amber-800">Doublons détectés</h3>
          <p className="text-sm text-amber-700 mt-1">
            {totalDuplicates} contacts dans votre fichier d&apos;import correspondent à des contacts existants
            dans votre base de données. Veuillez décider comment gérer ces doublons.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-medium">Gestion des doublons</h3>
            <p className="text-gray-600 mt-1">
              {resolvedCount}/{totalDuplicates} doublons traités
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleApplyToAll("merge")}
            >
              Fusionner tous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleApplyToAll("keep")}
            >
              Conserver tous les existants
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleApplyToAll("replace")}
            >
              Remplacer tous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleApplyToAll("skip")}
            >
              Ignorer tous les importés
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Contact existant</TableHead>
                <TableHead>Contact importé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {duplicates.map((duplicate, index) => (
                <TableRow key={`${duplicate.existing?.email}-${duplicate.imported?.email}-${index}`}>
                  <TableCell>
                    <Select
                      value={duplicate.action}
                      onValueChange={(value: any) => handleActionChange(index, value)}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Choisir" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="merge">
                          Fusionner (Compléter les manques)
                        </SelectItem>
                        <SelectItem value="keep">
                          Conserver l&apos;existant
                        </SelectItem>
                        <SelectItem value="replace">
                          Remplacer par l&apos;importé
                        </SelectItem>
                        <SelectItem value="skip">
                          Ignorer l&apos;importé
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">
                        {duplicate.existing?.firstname} {duplicate.existing?.lastname}
                      </div>
                      <div className="text-sm text-gray-500">{duplicate.existing?.email}</div>
                      {duplicate.existing?.phone && (
                        <div className="text-sm text-gray-500">{duplicate.existing.phone}</div>
                      )}
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Existant
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">
                        {duplicate.imported?.firstname} {duplicate.imported?.lastname}
                      </div>
                      <div className="text-sm text-gray-500">{duplicate.imported?.email}</div>
                      {duplicate.imported?.phone && (
                        <div className="text-sm text-gray-500">{duplicate.imported.phone}</div>
                      )}
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Nouveau
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleFinalize}>
          Finaliser l&apos;import
        </Button>
      </div>
    </div>
  );
};

export default DuplicateChecker;
